import { logout } from "@/api/auth";
import { useGetUser } from "@/api/users";
import { NAVIGATION_MENU } from "@/routes/NavigationMenu";
import { getStoredUserId, getUserPermissions } from "@/utils";
import { CloseOutlined, DownOutlined, LeftOutlined, MenuOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Dropdown, Layout, Menu, Space, Typography } from "antd";
import { useCallback, useMemo, useState } from "react";
import { FaRegUser } from "react-icons/fa";
import { MdLogout } from "react-icons/md";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import "./Layout.css";

const { Header, Content } = Layout;

export default function DashBoardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = getStoredUserId();
  const { data } = useGetUser(userId);
  const permissions = getUserPermissions(data);
  const username = data?.username || data?.email || "User";

  const hasPerm = (featureKey) => {
    if (!featureKey) return true;
    return permissions?.[featureKey] === true;
  };

  // Build AntD menu items and key->path map
  const { antMenuItems, keyToPathMap } = useMemo(() => {
    const build = (items) =>
      items
        .map((item) => {
          const children = item.submenu ? build(item.submenu) : [];
          const visible = hasPerm(item.featureKey) && (!!item.path || children.length > 0);
          if (!visible) return null;
          const hasChildren = children.length > 0;
          const labelNode = hasChildren
            ? (
              <span className="dropdown-trigger">
                {item.title} <DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
              </span>
            )
            : (item.path ? <Link to={item.path}>{item.title}</Link> : item.title);
          return {
            key: item.key,
            label: labelNode,
            icon: item.icon ? <item.icon /> : null,
            children: children.length ? children : undefined,
            _path: item.path || undefined,
          };
        })
        .filter(Boolean);
    const items = build(NAVIGATION_MENU);
    const map = {};
    const collect = (arr) => {
      arr.forEach((it) => {
        if (it._path) map[it.key] = it._path;
        if (it.children) collect(it.children);
      });
    };
    collect(items);
    return { antMenuItems: items, keyToPathMap: map };
  }, [permissions]);

  // Mobile tree data (uses subMenu key as per existing UI)
  const menuItems = useMemo(() => {
    const toMobile = (items) =>
      items
        .map((item) => {
          const children = item.submenu ? toMobile(item.submenu) : [];
          const visible = hasPerm(item.featureKey) && (!!item.path || children.length > 0);
          if (!visible) return null;
          return {
            key: item.key,
            title: item.title,
            path: item.path || null,
            icon: item.icon || null,
            subMenu: children,
          };
        })
        .filter(Boolean);
    return toMobile(NAVIGATION_MENU);
  }, [permissions]);

  const selectedKey = useMemo(() => {
    const walk = (items) => {
      for (const i of items) {
        if (i.path && location.pathname.startsWith(i.path.replace(":callId", ""))) return i.key;
        if (i.submenu && i.submenu.length) {
          const found = walk(i.submenu);
          if (found) return found;
        }
      }
      return "";
    };
    return walk(NAVIGATION_MENU);
  }, [location.pathname]);

  const handleMenuClick = useCallback((e) => {
    const path = keyToPathMap[e.key];
    if (path) navigate(path);
  }, [keyToPathMap, navigate]);

  // Compute page title/icon for param routes and normal routes
  const paramRouteMatch = useMemo(() => {
    const walk = (items) => {
      for (const i of items) {
        if (i.path && i.path.includes(":")) {
          const basePath = i.path.split("/:")[0];
          if (location.pathname.startsWith(basePath + "/")) {
            return {
              basePath,
              title: i.title,
              iconElement: i.icon ? <i.icon /> : null,
            };
          }
        }
        if (i.submenu && i.submenu.length) {
          const found = walk(i.submenu);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(NAVIGATION_MENU);
  }, [location.pathname]);

  const navigationMatch = useMemo(() => {
    let best = null;
    const walk = (items) => {
      for (const i of items) {
        if (i.path && location.pathname.startsWith(i.path.replace(":callId", ""))) {
          if (!best || (best.path && i.path.length > best.path.length)) best = i;
        }
        if (i.submenu && i.submenu.length) walk(i.submenu);
      }
    };
    walk(NAVIGATION_MENU);
    return best;
  }, [location.pathname]);

  const currentLabel = navigationMatch?.title || "Dashboard";
  const currentIcon = navigationMatch?.icon ? <navigationMatch.icon /> : null;

  // Mobile nav state
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const toggleGroupExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const closeMobileNav = useCallback(() => setIsMobileNavOpen(false), []);

  const renderMobileTree = useCallback((nodes) => {
    if (!nodes || nodes.length === 0) return null;
    return nodes.map((item) => {
      const hasChildren = Array.isArray(item.subMenu) && item.subMenu.length > 0;
      const key = hasChildren ? `group:${item.title}` : item.path;
      const IconEl = item.icon ? <item.icon /> : null;
      if (hasChildren) {
        const expanded = expandedKeys.has(key);
        return (
          <div key={key} className="mobile-nav-group">
            <button
              className="mobile-nav-row"
              aria-expanded={expanded}
              onClick={() => toggleGroupExpanded(key)}
            >
              <span className="mobile-nav-chevron">{expanded ? <DownOutlined /> : <RightOutlined />}</span>
              {IconEl && <span className="mobile-nav-icon">{IconEl}</span>}
              <span className="mobile-nav-title">{item.title}</span>
            </button>
            {expanded && (
              <div className="mobile-nav-children">
                {renderMobileTree(item.subMenu)}
              </div>
            )}
          </div>
        );
      }
      return (
        <button
          key={key}
          className="mobile-nav-row mobile-nav-leaf"
          onClick={() => {
            navigate(item.path);
            closeMobileNav();
          }}
        >
          {IconEl && <span className="mobile-nav-icon">{IconEl}</span>}
          <span className="mobile-nav-title">{item.title}</span>
        </button>
      );
    });
  }, [expandedKeys, navigate, closeMobileNav, toggleGroupExpanded]);

  const userMenu = (
    {
      items: [
        {
          key: 'logout',
          label: (
            <div onClick={() => {
              logout();
              navigate("/auth/login");
            }}>
              <MdLogout style={{ marginRight: 8 }} />
              Logout
            </div>
          ),
        }
      ]
    }
  );

  return (
    <Layout style={{ minHeight: "100vh", width: "100%" }}>
      <Layout>
        {/* Top Header */}
        <Header className="dashboard-header">
          <div className="header-left">
            <img src="/logo.svg" alt="VOC Logo" className="logo-icon" />
          </div>
          <div className="header-center">
            <Menu
              mode="horizontal"
              selectedKeys={[selectedKey]}
              items={antMenuItems}
              onClick={handleMenuClick}
              className="dashboard-menu"
              triggerSubMenuAction="hover"
            />
          </div>
          <div className="header-right">
            <Space size="middle">
              <Button
                type="text"
                className="hamburger-btn"
                aria-label="Open menu"
                onClick={() => setIsMobileNavOpen(true)}
              >
                <MenuOutlined />
              </Button>
              <Dropdown menu={userMenu} trigger={['hover']} overlayStyle={{ zIndex: 1300 }} placement="bottomRight" destroyOnHidden>
                <Button
                  type="text"
                  size="default"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px',
                  }}
                >
                  <FaRegUser style={{ marginRight: 8 }} />
                  <span style={{ marginRight: 4 }}>{username}</span>
                  {/* Keep caret style consistent */}
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}><DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }}/></span>
                </Button>
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* Secondary Header with Page Title */}
        <Header className="dashboard-header-bottom">
          {paramRouteMatch ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', width: '100%' }}>
              <Button
                type="text"
                aria-label="Back"
                title="Back"
                onClick={() => navigate(paramRouteMatch.basePath)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  fontSize: '16px'
                }}
              >
                <LeftOutlined />
              </Button>

              <Typography.Title level={4} style={{ margin: 0, color: '#333' }}>
                {paramRouteMatch.iconElement || currentIcon ? (
                  <span className="dashboard-header-icon">{paramRouteMatch.iconElement || currentIcon}</span>
                ) : null}
                <span className="dashboard-header-text">{paramRouteMatch.title || currentLabel}</span>
              </Typography.Title>
            </div>
          ) : (
            <Typography.Title level={4} style={{ margin: 0, color: '#333' }}>
              {currentIcon && (
                <span className="dashboard-header-icon">{currentIcon}</span>
              )}
              <span className="dashboard-header-text">{currentLabel}</span>
            </Typography.Title>
          )}
        </Header>

        {/* Main Content */}
        <Content className="dashboard-content">
          <Outlet />
        </Content>
      </Layout>
      {isMobileNavOpen && (
        <div className="mobile-nav-overlay" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <div className="mobile-nav-container">
            <div className="mobile-nav-header">
              <img src="/logo.svg" alt="VOC Logo" className="logo-icon" />
              <button
                className="mobile-nav-close"
                aria-label="Close menu"
                onClick={closeMobileNav}
              >
                <CloseOutlined />
              </button>
            </div>
            <nav className="mobile-nav-tree">
              {renderMobileTree(menuItems)}
            </nav>
          </div>
        </div>
      )}
    </Layout>
  );
}
